import {
    CORE_SIZE__SINGLEMODE,
    MAX_EXTERNAL_SWITCH_CHANNEL,
    MAX_INTERNAL_SWITCH_CHANNEL,
    MIN_EXTERNAL_SWITCH_CHANNEL,
    MIN_INTERNAL_SWITCH_CHANNEL,
    MTJ__DOUBLE,
    TJ_TYPE__MANUAL,
    TJ_TYPE__MEASURE,
    TJ_TYPE__AUTO,
    TJ_TYPE__USER_INPUT,
    TERMINATION_TYPE__CONNECTOR,
    TERMINATION_TYPE__NOT_SPECIFIED,
    TERMINATION_TYPE__BARE_FIBER
} from '@shared/constants/constants';

// monitor field changes and trigger appropriate validation
export function triggerValidation(curState, prevState, setState) {
    // Global validation (prevents test plan creation/update on failure)
    if (
        curState.validation.dutConfigPage !== prevState.validation.dutConfigPage ||
        curState.validation.setupPage !== prevState.validation.setupPage ||
        curState.validation.measurementPage !== prevState.validation.measurementPage ||
        curState.validation.labelPage !== prevState.validation.labelPage ||
        curState.validation.reviewPage !== prevState.validation.reviewPage
    ) {
        setState((state) => ({
            validation: {
                ...state.validation,
                wizard:
                    state.validation.dutConfigPage &&
                    state.validation.setupPage &&
                    state.validation.measurementPage &&
                    state.validation.labelPage &&
                    state.validation.reviewPage
            }
        }));
    }

    // DUT Config page validation (DUT Config tab turns red on failure)
    if (
        curState.validation.fields.fibersPerCable !== prevState.validation.fields.fibersPerCable ||
        curState.validation.fields.cablesPerAssembly !== prevState.validation.fields.cablesPerAssembly ||
        curState.validation.fields.terminationTypeEndA !== prevState.validation.fields.terminationTypeEndA ||
        curState.validation.fields.terminationTypeEndB !== prevState.validation.fields.terminationTypeEndB
    ) {
        setState((state) => ({
            validation: {
                ...state.validation,
                dutConfigPage:
                    state.validation.fields.fibersPerCable &&
                    state.validation.fields.cablesPerAssembly &&
                    state.validation.fields.terminationTypeEndA &&
                    state.validation.fields.terminationTypeEndB
            }
        }));
    }

    // Setup page validation (setup tab turns red on failure)
    if (curState.validation.fields.tests !== prevState.validation.fields.tests) {
        setState((state) => ({ validation: { ...state.validation, setupPage: state.validation.fields.tests } }));
    }

    // IL/RL page validation (IL/RL tab turns red on failure)
    if (
        curState.validation.fields.switch0 !== prevState.validation.fields.switch0 ||
        curState.validation.fields.switch1 !== prevState.validation.fields.switch1 ||
        curState.validation.fields.switch2 !== prevState.validation.fields.switch2 ||
        curState.validation.fields.riValid !== prevState.validation.fields.riValid ||
        curState.validation.fields.ilCompValid !== prevState.validation.fields.ilCompValid ||
        curState.validation.fields.tj1Length !== prevState.validation.fields.tj1Length ||
        curState.validation.fields.tj2Length !== prevState.validation.fields.tj2Length ||
        curState.validation.fields.wavelengths !== prevState.validation.fields.wavelengths
    ) {
        setState((state) => ({
            validation: {
                ...state.validation,
                measurementPage:
                    state.validation.fields.switch0.every((v) => v) &&
                    state.validation.fields.switch1.every((v) => v) &&
                    state.validation.fields.switch2.every((v) => v) &&
                    state.validation.fields.tj1Length.every((v) => v) &&
                    state.validation.fields.tj2Length.every((v) => v) &&
                    state.validation.fields.riValid &&
                    state.validation.fields.ilCompValid &&
                    state.validation.fields.wavelengths
            }
        }));
    }

    // Label page validation (name tab turns red on failure)
    if (curState.validation.fields.customField !== prevState.validation.fields.customField) {
        setState((state) => ({ validation: { ...state.validation, labelPage: state.validation.fields.customField } }));
    }

    // Review page validation (review tab turns red on failure)
    if (curState.validation.fields.name !== prevState.validation.fields.name) {
        setState((state) => ({ validation: { ...state.validation, reviewPage: state.validation.fields.name } }));
    }

    //
    // DUT Config page
    //

    // fibersPerCable
    if (curState.fibersPerCable !== prevState.fibersPerCable) {
        validateFibersPerCable(setState);
    }

    // cablesPerAssembly
    if (curState.cablesPerAssembly !== prevState.cablesPerAssembly) {
        validateCablesPerAssembly(setState);
    }

    if(curState.assembly.terminationTypeEndA !== prevState.assembly.terminationTypeEndA ||
       curState.assembly.terminationTypeEndB !== prevState.assembly.terminationTypeEndB) {
        validateTerminationTypes(setState);
    }

    //
    // Setup page
    //

    // tests
    // TODO: No wavelength is needed if no IL or RL
    if (
        curState.assembly.insertionLossSideA !== prevState.assembly.insertionLossSideA ||
        curState.assembly.insertionLossSideB !== prevState.assembly.insertionLossSideB ||
        curState.assembly.returnLossSideA !== prevState.assembly.returnLossSideA ||
        curState.assembly.returnLossSideB !== prevState.assembly.returnLossSideB ||
        curState.assembly.returnLossTotal !== prevState.assembly.returnLossTotal ||
        curState.assembly.insertionLossTotal !== prevState.assembly.insertionLossTotal ||
        curState.assembly.testPolarity !== prevState.assembly.testPolarity
    ) {
        validateTests(setState);
    }

    //
    // IL/RL page
    //

    // wavelengths
    // TODO: No wavelength is needed if no IL or RL
    if (
        curState.wavelength_850 !== prevState.wavelength_850 ||
        curState.wavelength_1300 !== prevState.wavelength_1300 ||
        curState.wavelength_1310 !== prevState.wavelength_1310 ||
        curState.wavelength_1490 !== prevState.wavelength_1490 ||
        curState.wavelength_1550 !== prevState.wavelength_1550 ||
        curState.wavelength_1625 !== prevState.wavelength_1625 ||
        curState.assembly.coreSize !== prevState.assembly.coreSize
    ) {
        validateWavelengths(setState);
    }

    // switch0 (output)
    if (curState.equipment.switch0 !== prevState.equipment.switch0) {
        validateSwitch0(setState);
    }

    // switch1
    if (curState.equipment.switch1 !== prevState.equipment.switch1) {
        validateSwitch1(setState);
    }

    // switch2
    if (curState.equipment.switch2 !== prevState.equipment.switch2) {
        validateSwitch2(setState);
    }

    // Tj1 length
    if (
        (curState.equipment.tj1Length !== prevState.equipment.tj1Length && curState.equipment.tj1 === TJ_TYPE__MANUAL) ||
        curState.equipment.tj1 !== prevState.equipment.tj1
    ) {
        validateTj1(setState);
    }

    // Tj2 length
    if (
        (curState.equipment.tj2Length !== prevState.equipment.tj2Length && curState.equipment.tj2 === TJ_TYPE__MANUAL) ||
        curState.equipment.tj2 !== prevState.equipment.tj2
    ) {
        validateTj2(setState);
    }

    //
    // Label page
    //

    // customFields
    if (curState.customFields !== prevState.customFields) {
        validateCustomFields(setState);
    }

    //
    // Review page
    //

    // name
    if (curState.name !== prevState.name) {
        validateName(setState);
    }
}

// triggers validation on the entire wizard
export function validateWizard(setState) {
    validateDUTConfigPage(setState);
    validateSetupPage(setState);
    validateILRLPage(setState);
    validateLabelPage(setState);
    validateReviewPage(setState);
}

export function validateDUTConfigPage(setState) {
    validateFibersPerCable(setState);
    validateCablesPerAssembly(setState);
    validateTerminationTypes(setState);
}

// triggers validation on the setup page
export function validateSetupPage(setState) {
    validateTests(setState);
}

// triggers validation on the IL/RL page
export function validateILRLPage(setState) {
    validateSwitch0(setState);
    validateSwitch1(setState);
    validateSwitch2(setState);
    validateTj1(setState);
    validateTj2(setState);
    validateCables(setState);
    validateIlComp(setState);
    validateWavelengths(setState);
}

// triggers validation on the options page
export function validateLabelPage(setState) {
    validateCustomFields(setState);
}

// triggers validation on the review page
export function validateReviewPage(setState) {
    validateName(setState);
}

//
// DUT Config page
//

// fibersPerCable
function validateFibersPerCable(setState) {
    setState((state) => {
        let isValid = Number.isInteger(state.fibersPerCable) && state.fibersPerCable > 0;

        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, fibersPerCable: isValid } }
        };
    });
}

// cablesPerAssembly
function validateCablesPerAssembly(setState) {
    setState((state) => {
        let isValid = Number.isInteger(state.cablesPerAssembly) && state.cablesPerAssembly > 0;

        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, cablesPerAssembly: isValid } }
        };
    });
}

// termination types
function validateTerminationTypes(setState) {
    setState((state) => {
        let endAIsValid = state.assembly.terminationTypeEndA !== TERMINATION_TYPE__CONNECTOR && state.assembly.terminationTypeEndA !== TERMINATION_TYPE__NOT_SPECIFIED && state.assembly.terminationTypeEndA !== TERMINATION_TYPE__BARE_FIBER;
        let endBIsValid = state.assembly.terminationTypeEndB !== TERMINATION_TYPE__CONNECTOR && state.assembly.terminationTypeEndB !== TERMINATION_TYPE__NOT_SPECIFIED;

        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, terminationTypeEndA: endAIsValid, terminationTypeEndB: endBIsValid } }
        };
    });
}

//
// Setup page
//

// tests
function validateTests(setState) {
    setState((state) => {
        // At least one test must be selected
        let isValid =
            state.assembly.insertionLossSideA ||
            state.assembly.insertionLossSideB ||
            state.assembly.returnLossSideA ||
            state.assembly.returnLossSideB ||
            state.assembly.returnLossTotal ||
            state.assembly.insertionLossTotal ||
            state.assembly.testPolarity;

        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, tests: isValid } }
        };
    });
}

//
// IL/RL page
//

// wavelengths
function validateWavelengths(setState) {
    setState((state) => {
        // At least one wavelength must be selected
        // TODO: If no IL or RL, wavelength is not necessary
        let isValid =
            state.assembly.coreSize === CORE_SIZE__SINGLEMODE
                ? state.wavelength_1310 || state.wavelength_1490 || state.wavelength_1550 || state.wavelength_1625
                : state.wavelength_850 || state.wavelength_1300;

        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, wavelengths: isValid } }
        };
    });
}

// switch0 (output)
function validateSwitch0(setState) {
    setState((state) => {
        let isValidSwitch0 = [];
        state.equipment.switch0.forEach((channel, i) => {
            isValidSwitch0[i] = channel >= MIN_INTERNAL_SWITCH_CHANNEL && channel <= MAX_INTERNAL_SWITCH_CHANNEL;
        });
        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, switch0: isValidSwitch0 } }
        };
    });
}

// switch1
function validateSwitch1(setState) {
    setState((state) => {
        let isValidSwitch1 = [];
        state.equipment.switch1.forEach((channel, i) => {
            if (state.equipment.useSwitch1) {
                if (channel === null || channel === '') {
                    if (state.equipment.useSwitch2) {
                        if (state.equipment.switch2[i] === null || state.equipment.switch2[i] === '') {
                            isValidSwitch1[i] = false;
                        } else {
                            isValidSwitch1[i] = true;
                        }
                    } else {
                        isValidSwitch1[i] = false;
                    }
                } else {
                    isValidSwitch1[i] = channel >= MIN_EXTERNAL_SWITCH_CHANNEL && channel <= MAX_EXTERNAL_SWITCH_CHANNEL;
                }
            } else {
                isValidSwitch1[i] = true;
            }
        });

        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, switch1: isValidSwitch1 } }
        };
    });
}

// switch2
function validateSwitch2(setState) {
    setState((state) => {
        let isValidSwitch2 = [];
        state.equipment.switch2.forEach((channel, i) => {
            if (state.equipment.useSwitch2) {
                if (channel === null || channel === '') {
                    if (state.equipment.useSwitch2) {
                        if (state.equipment.switch2[i] === null || state.equipment.switch2[i] === '') {
                            isValidSwitch2[i] = false;
                        } else {
                            isValidSwitch2[i] = true;
                        }
                    } else {
                        isValidSwitch2[i] = false;
                    }
                } else {
                    isValidSwitch2[i] = channel >= MIN_EXTERNAL_SWITCH_CHANNEL && channel <= MAX_EXTERNAL_SWITCH_CHANNEL;
                }
            } else {
                isValidSwitch2[i] = true;
            }
        });

        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, switch2: isValidSwitch2 } }
        };
    });
}

function validateTj1(setState) {
    setState((state) => {
        let isValidTj1 = [];
        state.equipment.tj1Length.forEach((channel, i) => {
            if (state.equipment.tj1 === TJ_TYPE__MEASURE || state.equipment.tj1 === TJ_TYPE__USER_INPUT) {
                isValidTj1[i] = true;
            } else {
                if (channel === null || channel === '') {
                    isValidTj1[i] = false;
                } else {
                    isValidTj1[i] = true;
                }
            }
        });

        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, tj1Length: isValidTj1 } }
        };
    });
}

function validateTj2(setState) {
    setState((state) => {
        let isValidTj2 = [];
        state.equipment.tj2Length.forEach((channel, i) => {
            if (state.equipment.mtjConfiguration === MTJ__DOUBLE) {
                if (state.equipment.tj2 === TJ_TYPE__AUTO) {
                    isValidTj2[i] = true;
                } else {
                    if (channel === null || channel === '') {
                        isValidTj2[i] = false;
                    } else {
                        isValidTj2[i] = true;
                    }
                }
            } else {
                isValidTj2[i] = true;
            }
        });

        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, tj2Length: isValidTj2 } }
        };
    });
}

function validateCables(setState) {
    setState((state) => {
        let isValid = true;
        if (state.assembly.insertionLossSideA && state.equipment.mtjConfiguration !== MTJ__DOUBLE) {
            if (state.assembly.sideA.il.length !== state.fibersPerCable * state.cablesPerAssembly) {
                isValid = false;
            }
            if (state.assembly.sideA.il.includes(null)) {
                isValid = false;
            }
        }

        if (state.assembly.insertionLossSideB === true && state.equipment.mtjConfiguration !== MTJ__DOUBLE) {
            if (state.assembly.sideB.il.length !== state.fibersPerCable * state.cablesPerAssembly) {
                isValid = false;
            }
            if (state.assembly.sideB.il.includes(null)) {
                isValid = false;
            }
        }
        if (
            state.equipment.mtjConfiguration === MTJ__DOUBLE && state.assembly.insertionLossTotal
        ) {
            if (state.assembly.ilTotalList.length !== state.fibersPerCable * state.cablesPerAssembly) {
                isValid = false;
            }
            if (state.assembly.ilTotalList.includes(null)) {
                isValid = false;
            }
        }
        if (state.assembly.returnLossSideA === true) {
            if (state.assembly.sideA.rl.length !== state.fibersPerCable * state.cablesPerAssembly) {
                isValid = false;
            }
            if (state.assembly.sideA.rl.includes(null)) {
                isValid = false;
            }
        }
        if (state.assembly.returnLossSideB === true) {
            if (state.assembly.sideB.rl.length !== state.fibersPerCable * state.cablesPerAssembly) {
                isValid = false;
            }
            if (state.assembly.sideB.rl.includes(null)) {
                isValid = false;
            }
        }
        if (state.assembly.returnLossTotal === true) {
            if (state.assembly.sideA.rlTotal.length !== state.fibersPerCable * state.cablesPerAssembly) {
                isValid = false;
            }
            if (state.assembly.sideB.rlTotal.length !== state.fibersPerCable * state.cablesPerAssembly) {
                isValid = false;
            }
            if (state.assembly.sideA.rlTotal.includes(null) || state.assembly.sideB.rlTotal.includes(null)) {
                isValid = false;
            }
        }
        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, riValid: isValid } }
        };
    });
}

//IL compensation
function validateIlComp(setState) {
    setState((state) => {
        let isValid = true;
        if (state.ilComp.enable) {
            if (state.wavelength_850) {
                if (state.ilComp.attenuation_850 === null) {
                    isValid = false;
                }
            }
            if (state.wavelength_1300) {
                if (state.ilComp.attenuation_1300 === null) {
                    isValid = false;
                }
            }
            if (state.wavelength_1310) {
                if (state.ilComp.attenuation_1310 === null) {
                    isValid = false;
                }
            }
            if (state.wavelength_1490) {
                if (state.ilComp.attenuation_1490 === null) {
                    isValid = false;
                }
            }

            if (state.wavelength_1550) {
                if (state.ilComp.attenuation_1550 === null) {
                    isValid = false;
                }
            }
            if (state.wavelength_1625) {
                if (state.ilComp.attenuation_1625 === null) {
                    isValid = false;
                }
            }
        }

        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, ilCompValid: isValid } }
        };
    });
}

//
// Label page
//
function validateCustomFields(setState) {
    setState((state) => {
        let isValid = true;
        if (state.customFields.length !== 0) {
            state.customFields.forEach((field) => {
                if (!('id' in field)) {
                    isValid = false;
                }
            });
        }
        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, customField: isValid } }
        };
    });
}

//
// Review page
//

// name
function validateName(setState) {
    setState((state) => {
        // Name must not be empty

        let isValid = typeof state.name !== 'undefined' && state.name.trim() !== '';

        return {
            validation: { ...state.validation, fields: { ...state.validation.fields, name: isValid } }
        };
    });
}
